import BaseBubbleController from "./BaseBubbleController.js";
import {
  escapeBubbleHtml,
  formatBubbleTimestamp,
  renderBubbleFooter,
  renderMarkdownBubblePanel,
  renderMarkdownContent,
} from "./bubbleControllerHtml.js";

function normalizeFilesWithNames(files = [], fileNames = []) {
  return files.map((fileContent, index) => {
    const fileName = Array.isArray(fileNames) ? fileNames[index] : fileNames;
    if (typeof fileContent === "string") {
      return {
        content: fileContent,
        name: fileName || `file-${index + 1}`,
      };
    }

    if (fileContent && typeof fileContent === "object") {
      return {
        ...fileContent,
        name: fileContent.name || fileName || `file-${index + 1}`,
      };
    }

    return {
      content: fileContent,
      name: fileName || `file-${index + 1}`,
    };
  });
}

export default class UserMessageBubbleController extends BaseBubbleController {
  constructor() {
    super("user-message", "user");
  }

  createMessageBubbles({
    text = "",
    images = [],
    files = [],
    fileNames = [],
    timestamp = new Date(),
    messageId,
    restoreState = {},
    isDisabled = false,
    isMutationBlocked = false,
  } = {}) {
    const bubbles = [];

    if (Array.isArray(images) && images.length > 0) {
      bubbles.push(
        this.createBubble({
          kind: "images",
          role: "user",
          content: images,
          timestamp,
          messageId,
          isDisabled,
          isMutationBlocked,
          ...restoreState,
        })
      );
    }

    if (Array.isArray(files) && files.length > 0) {
      bubbles.push(
        this.createBubble({
          kind: "files",
          role: "user",
          content: files,
          fileName: fileNames,
          timestamp,
          messageId,
          isDisabled,
          isMutationBlocked,
          ...restoreState,
        })
      );
    }

    if (text) {
      bubbles.push(
        this.createBubble({
          kind: "text",
          role: "user",
          content: text,
          timestamp,
          messageId,
          isDisabled,
          isMutationBlocked,
          ...restoreState,
        })
      );
    }

    const lastIndex = bubbles.length - 1;
    return bubbles.map((bubble, index) =>
      this.updateBubble(bubble, {
        showTimestamp: index === lastIndex,
        showRestore: index === lastIndex && Boolean(bubble.showRestore),
        isRedo: index === lastIndex ? bubble.isRedo : false,
        onRestoreClick: index === lastIndex ? bubble.onRestoreClick : null,
      })
    );
  }

  renderBubbleHtml(bubble) {
    const panelHtml = this.renderPanelHtml(bubble);
    const footerHtml = renderBubbleFooter({
      role: "user",
      timestamp: bubble.showTimestamp
        ? formatBubbleTimestamp(bubble.timestamp)
        : "",
      footerActionsHtml: bubble.showRestore
        ? `
            <button
              class="bubble-controller-footer-action"
              type="button"
              data-restore-button="true"
              ${
                bubble.isDisabled ||
                bubble.isMutationBlocked ||
                bubble.isRestorePending
                  ? "disabled"
                  : ""
              }
            >
              ${bubble.isRedo ? "Redo" : "Restore"}
            </button>
          `.trim()
        : "",
    });

    return [panelHtml, footerHtml].filter(Boolean).join("");
  }

  renderPanelHtml(bubble) {
    if (bubble.kind === "images") {
      return this.renderImagesPanel(bubble);
    }

    if (bubble.kind === "files") {
      return this.renderFilesPanel(bubble);
    }

    return renderMarkdownBubblePanel({
      role: "user",
      html: renderMarkdownContent(bubble.content || ""),
      extraClassName: "bubble-controller-panel-text",
    });
  }

  renderImagesPanel(bubble) {
    const images = Array.isArray(bubble.content) ? bubble.content : [];
    const imageItemsHtml = images
      .map(
        (src, index) => `
          <button
            class="bubble-controller-media-item"
            type="button"
            data-image-button="true"
            data-image-index="${index}"
          >
            <img src="${escapeBubbleHtml(
              src
            )}" alt="User attachment ${index + 1}" class="bubble-controller-media-image" />
          </button>
        `.trim()
      )
      .join("");

    return `
      <div class="bubble-controller-panel bubble-controller-panel-user bubble-controller-panel-media" data-bubble-context-menu-target="true">
        <div class="bubble-controller-media-grid">${imageItemsHtml}</div>
      </div>
    `.trim();
  }

  renderFilesPanel(bubble) {
    const files = normalizeFilesWithNames(bubble.content || [], bubble.fileName);
    const fileItemsHtml = files
      .map(
        (file, index) => `
          <button
            class="bubble-controller-file-item"
            type="button"
            data-file-button="true"
            data-file-index="${index}"
          >
            <span class="bubble-controller-file-name">${escapeBubbleHtml(
              file?.name || `file-${index + 1}`
            )}</span>
          </button>
        `.trim()
      )
      .join("");

    return `
      <div class="bubble-controller-panel bubble-controller-panel-user bubble-controller-panel-files" data-bubble-context-menu-target="true">
        <div class="bubble-controller-file-list">${fileItemsHtml}</div>
      </div>
    `.trim();
  }

  getContent(bubble) {
    if (bubble.kind === "text") {
      return bubble.content || "";
    }

    if (bubble.kind === "images") {
      return Array.isArray(bubble.content) ? bubble.content.join("\n") : "";
    }

    const files = normalizeFilesWithNames(bubble.content || [], bubble.fileName);
    return files.map((file) => file?.name || "").join("\n");
  }

  onBubbleRendered(bubble, uiApi) {
    uiApi?.queryAll?.("[data-restore-button]").forEach((button) => {
      if (button.dataset.controllerBound === "true") {
        return;
      }
      button.dataset.controllerBound = "true";
      button.addEventListener("click", () => bubble.onRestoreClick?.());
    });

    uiApi?.queryAll?.("[data-image-button]").forEach((button) => {
      if (button.dataset.controllerBound === "true") {
        return;
      }
      button.dataset.controllerBound = "true";
      button.addEventListener("click", () => {
        const imageIndex = Number.parseInt(button.dataset.imageIndex ?? "-1", 10);
        if (imageIndex >= 0) {
          uiApi?.handleImageClick?.(
            bubble.bubbleId,
            imageIndex,
            Array.isArray(bubble.content) ? bubble.content : []
          );
        }
      });
    });

    uiApi?.queryAll?.("[data-file-button]").forEach((button) => {
      if (button.dataset.controllerBound === "true") {
        return;
      }
      button.dataset.controllerBound = "true";
      button.addEventListener("click", () => {
        const fileIndex = Number.parseInt(button.dataset.fileIndex ?? "-1", 10);
        if (fileIndex >= 0) {
          uiApi?.handleFileClick?.(
            bubble.bubbleId,
            fileIndex,
            normalizeFilesWithNames(bubble.content || [], bubble.fileName)
          );
        }
      });
    });
  }
}
